import { Subject, BehaviorSubject, of, catchError, Observable, map, throwIfEmpty, interval, switchMap } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { webSocket } from 'rxjs/webSocket';
import { Logger } from 'tslog';
import { ConnectionState } from './connection-state';
import { takeWhile } from 'rxjs/operators';
const log = new Logger({ prefix: ['[sdk-finvu]'] });
export class FinvuService {
    toUrl(protocol, host, uri) {
        return new URL(uri, `${protocol}://${host}`).toString();
    }
    constructor(opts) {
        this.opts = opts;
        this.messages = new Subject();
        this.connStatus = new BehaviorSubject(ConnectionState.INIT);
        this._conState = ConnectionState.INIT;
        this.checkReadiness = (resolve, reject, retry) => {
            if (this.isReady) {
                resolve();
            }
            else {
                const dontWaitInfinitely = !this.waitInfiniteForSend;
                if (dontWaitInfinitely && retry <= 0) {
                    // Hmm, something really has gone wrong?
                    // If retry was initially 100 => We've waited for 25 seconds now! 
                    // Maybe be next time it'll connect?
                    // reject and let the sdk fail
                    reject();
                }
                else {
                    setTimeout(() => this.checkReadiness(resolve, reject, --retry), 250);
                }
            }
        };
        this.reconnectAttempts = parseInt(opts.reconnectAttempts) || 10;
        this.reconnectInterval = parseInt(opts.reconnectInterval) || 2000;
        this.waitInfiniteForSend = true; // Default is to wait for infinity to send! 🤯
        const flag = this.opts['wait_infinite_on_send'];
        if (flag) { // if the flag exists, use it!
            this.waitInfiniteForSend = flag.toLowerCase() === 'true';
        }
        this._connectionInitializer = this._setupConnector();
        this.config = {
            url: this.toUrl('wss', opts.host, opts.uri),
            openObserver: {
                next: (e) => {
                    this.connStatus.next(ConnectionState.CONNECTED);
                    log.debug('WebSocket connection established');
                },
            },
            closeObserver: {
                next: (e) => {
                    if (!e.wasClean) {
                        this.connStatus.next(ConnectionState.DISCONNECTED);
                        log.fatal('Websocket disconnected abrubtly');
                    }
                    else {
                        this.connStatus.next(ConnectionState.CLOSED);
                        log.debug('WebSocket connection gracefully closed');
                    }
                    this.connection = null; // clear the connection variable
                    this._clearConnectionResources(); // cleanup other connection related stuff
                },
            },
        };
        // Subscribe to response messages from server
        this.messagesSub = this.messages.subscribe({
            next: () => {
                // TODO: perform some kind of metric collection here, if required
            },
            error: (e) => log.error('Error in connection', e),
        });
        // Subscribe to connection status updates
        this.connStatusSub = this.connStatus.subscribe((status) => {
            this._conState = status;
            if (this._conState === ConnectionState.DISCONNECTED) {
                this.reconnect();
            }
        });
    }
    buildPath(path, params) {
        const used = new Set();
        // Replace the parts in [xxx]
        path = path.replace(/\[([^\]]+)]/g, (m, c0) => {
            used.add(c0);
            return c0 in params ? params[c0] : '';
        });
        // Add query string if there are any left over
        let qstr = Object.entries(params)
            .filter(([key]) => !used.has(key))
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');
        return path + (qstr && '?' + qstr);
    }
    get isReady() {
        return this._conState === ConnectionState.CONNECTED;
    }
    get isDisconnected() {
        return this._conState === ConnectionState.DISCONNECTED;
    }
    _setupConnector() {
        const httpUrl = this.toUrl('https', this.opts.host, this.opts.uri);
        const req = new Request(httpUrl, {
            credentials: 'include',
            mode: 'cors',
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: '', // TODO: check if we need to resolve this as well?
        });
        return fromFetch(req).pipe(map((response) => {
            if (response.ok) {
                // OK return data
                this._setupWebSocket();
                return true; // yay, got a resilient connection here
            }
            else {
                // Server is returning a status requiring the client to try something else.
                this._setupWebSocket();
                return false; // not a resilient connection
            }
        }), throwIfEmpty(), catchError((err) => {
            // Network or other error, handle appropriately
            log.error("Failed to configure a resilient connection, falling back to standard connection mode");
            this._setupWebSocket();
            return of(false); // nope, not a resilient connection 💩
        }));
    }
    connect() {
        if (this._conState === ConnectionState.CONNECTING || this._conState === ConnectionState.RECONNECTING) {
            return;
        } // Already connecting, don't try again
        // trigger connection setup
        this._connectionInitializer.subscribe(resilientMode => {
            if (resilientMode) {
                log.info("Connected in resilient connection mode");
            }
            else {
                log.info("Connected in standard connection mode");
            }
        });
    }
    reconnect() {
        var _a;
        this._conState = ConnectionState.RECONNECTING;
        this.reconnectionObservable = interval(this.reconnectInterval).pipe(takeWhile(index => { return index < this.reconnectAttempts && !this.isReady; }));
        (_a = this.reconnectionObservable) === null || _a === void 0 ? void 0 : _a.subscribe({
            next: () => {
                this.connect();
            },
            complete: () => this.reconnectionObservable = null
        });
    }
    _setupWebSocket(status = ConnectionState.CONNECTING) {
        this._conState = status;
        this.connection = webSocket(this.config);
        this.connectionSub = this.connection.subscribe({
            next: (msg) => this.messages.next(msg),
            error: (err) => log.error(err),
            complete: () => log.silly('complete'), // Called when connection is closed (for whatever reason).
        });
    }
    on(eventType) {
        if (eventType) {
            return this.messages.pipe(switchMap((msg) => {
                return new Observable((observer) => {
                    observer.next(msg);
                });
            }));
        }
    }
    async send(msg) {
        var _a;
        if (!this.isReady) {
            // Try re-initiating the connection
            this._connectionInitializer.subscribe(resilientMode => {
                if (resilientMode) {
                    log.info("Re-connected in resilient connection mode");
                }
                else {
                    log.info("Re-connected in standard connection mode");
                }
            });
            await new Promise((resolve, reject) => this.checkReadiness(resolve, reject, 100));
        }
        (_a = this.connection) === null || _a === void 0 ? void 0 : _a.next(msg);
    }
    _clearConnectionResources() {
        if (this.connectionSub) {
            this.connectionSub.unsubscribe();
            this.connStatusSub = null;
        }
    }
    cleanup() {
        this._clearConnectionResources();
        if (this.connStatusSub) {
            this.connStatusSub.unsubscribe();
            this.connStatusSub = null;
        }
        if (this.messagesSub) {
            this.messagesSub.unsubscribe();
            this.messagesSub = null;
        }
    }
}
//# sourceMappingURL=backend.js.map